import { Heading, Text, FormControl, Button, Box, Flex, FormLabel, Input, Tooltip } from "@chakra-ui/react";
import PageHeader from "components/layout/PageHeader";
import { BorrowerSelect } from "components/loans/CreateNewLoanApplication/components/BorrowerSelect";
import { LoanTypeSelect } from "components/loans/CreateNewLoanApplication/components/LoanTypeSelect";
import { PropertySelect } from "components/loans/CreateNewLoanApplication/components/PropertySelect";
import { PropertyLienTypeSelect } from "components/loans/CreateNewLoanApplication/components/PropertyLienTypeSelect";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";
import ApplicationFields from "../ApplicationFields";
import { BorrowerForTheCompanyModel } from "hooks/useBorrowersForTheCompany";
import { PropertyForTheCompanyModel } from "hooks/usePropertiesForTheCompany";
import { LoanTypeForTheTenantModel } from "hooks/useLoanTypesForTheTenant";
import { PropertyLienTypeModel } from "hooks/usePropertyLienType";
import { useCreateLoan } from "modules/loan/mutations/use-loan-create";
import { InfoOutlineIcon } from "@chakra-ui/icons";

const CreateNewLoanApplication = () => {
  const { user } = useTenant();
  const { showError } = useContext(AlertsContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<LoanTypeForTheTenantModel | null>(null);
  const [name, setName] = useState("");
  const [property, setProperty] = useState<PropertyForTheCompanyModel | null>(null);

  const [borrower, setBorrower] = useState<BorrowerForTheCompanyModel | null>(null);
  const [propertyLienType, setPropertyLienType] = useState<PropertyLienTypeModel | null>(null);
  const [applicationFields, setApplicationFields] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState({});

  const { mutateAsync: createLoan } = useCreateLoan();

  useEffect(() => {
    const fetchApplicationFields = async () => {
      if (type?.id) {
        const response = await apiRequest("/supabase/loan_fields/select", {
          loan_type: type.id,
        });

        setApplicationFields(response.loan_fields);
      }
    };
    fetchApplicationFields();
  }, [type]);

  async function onSubmit() {
    try {
      setLoading(true);

      const { id: loanId } = await createLoan({
        name,
        loan_type: type?.id,
        borrower_id: borrower?.id,
        property_id: property?.id,
        property_lien_type: propertyLienType?.name,
        field_values: fieldValues,
      });

      navigate({
        pathname: `/loans/${loanId}`,
        search: `?editMode=true`,
      });
    } catch (err) {
      showError({
        title: "Error Creating Loan",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }

  function changeBorrower(borrower: BorrowerForTheCompanyModel | null) {
    setProperty(null);
    setBorrower(borrower);
  }

  return (
    <>
      <PageHeader title="New Application" />
      <Flex direction="row" justifyContent="center">
        <Box
          bg="white"
          borderRadius={"28px"}
          borderTopLeftRadius={"auto"}
          borderBottomLeftRadius={"auto"}
          p="22px"
          maxW="760px"
          textAlign="center"
          flex="3"
        >
          <Heading fontWeight="600" fontSize="17px" lineHeight="28px" mb="5px">
            Loan Application
          </Heading>
          <Text mb="22px" fontSize="14px">
            Choose the specific loan type to proceed with the application.
          </Text>
          <Box>
            <FormControl mb="26px" w="320px" mx="auto">
              <FormLabel fontSize="17px" fontWeight="200" textAlign="left" display="flex" alignItems="center" gap="6px">
                Loan Name
                <Tooltip label="Please add the specified name to the loan. If no name is available, use the address of the subject property associated with the loan instead." hasArrow>
                  <span tabIndex={0} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    <InfoOutlineIcon color="gray.400" boxSize="18px" />
                  </span>
                </Tooltip>
              </FormLabel>
              <Input
                data-cy="loan-name"
                variant="primary"
                value={name}
                placeholder="Enter a loan name or address..."
                onChange={ev => setName(ev.target.value)}
              />
            </FormControl>
            <FormControl mb="26px" w="320px" mx="auto">
              <FormLabel fontSize="17px" fontWeight="200" textAlign="left">
                Loan Type
              </FormLabel>
              <LoanTypeSelect loanType={type} setLoanType={setType} />
            </FormControl>

            {user.type === "lender" && (
              <FormControl mb="26px" w="320px" mx="auto">
                <FormLabel fontSize="17px" fontWeight="200" textAlign="left">
                  Borrower
                </FormLabel>
                <BorrowerSelect borrower={borrower} setBorrower={e => changeBorrower(e)} />
              </FormControl>
            )}

            {user.type === "lender" && borrower && (
              <FormControl mb="26px" w="320px" mx="auto">
                <FormLabel fontSize="17px" fontWeight="200" textAlign="left">
                  Property Associated with Loan
                </FormLabel>
                <PropertySelect
                  property={property}
                  setProperty={setProperty}
                  companyId={borrower?.company_id}
                />
              </FormControl>
            )}

            {user.type === "lender" && (
              <FormControl mb="26px" w="320px" mx="auto">
                <FormLabel fontSize="17px" fontWeight="200" textAlign="left">
                  Property Lein Type
                </FormLabel>
                <PropertyLienTypeSelect
                  propertyLienType={propertyLienType}
                  setPropertyLienType={setPropertyLienType}
                />
              </FormControl>
            )}

            <Box mb={6}>
              <ApplicationFields
                fields={applicationFields}
                values={fieldValues}
                setValues={setFieldValues}
              />
            </Box>

            <Button
              onClick={onSubmit}
              variant={getCustomButtonVariant(user)}
              w="320px"
              data-cy="start-application-btn"
              isLoading={loading}
              disabled={!type || (user.type === "lender" && !borrower)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Flex>
    </>
  );
};

export default CreateNewLoanApplication;
